import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { ToastrService } from 'ngx-toastr';
import { Observable, catchError, of, switchMap, take } from 'rxjs';
import { ArticleService } from '../../services/article.service';
import * as articleActions from '../actions/article.actions';
import * as userActions from '../actions/user.actions';

@Injectable()
export class ArticleEffects {
  actions$ = inject(Actions);
  articleService: ArticleService = inject(ArticleService);
  toastr: ToastrService = inject(ToastrService);
  router: Router = inject(Router);

  constructor() {}

  // effect to fetch use articles from AWS using http call
  getArticles$ = createEffect(() =>
    this.actions$.pipe(
      ofType(articleActions.getArticles),
      switchMap(
        async ({ uid }) =>
          await this.articleService
            .getArticles(uid)
            .then((response) => response)
      ),
      switchMap((response: Observable<any>) =>
        response.pipe(
          take(1),
          switchMap((response) =>
            of(
              articleActions.loadArticle({
                articles: response['result'],
              })
            )
          )
        )
      ),
      catchError((error) => {
        console.error(`Error fetching articles: ${JSON.stringify(error)}`);
        return of(articleActions.getArticlesFailure({ error }));
      })
    )
  );

  // effect to create a article
  create$ = createEffect(() =>
    this.actions$.pipe(
      ofType(articleActions.create),
      switchMap(
        async ({ article }) => await this.articleService.create(article)
      ),
      switchMap((article) => {
        this.toastr.success(
          'The Article has been published. It might take a while for subscribers to view.',
          'Article Created Successfully'
        );
        this.router.navigate(['/articles']);
        return of(articleActions.createSuccess({ article }));
      }),
      catchError((error) => {
        // console.error(`Error creating article: ${error}`);
        this.toastr.error(
          `An error occurred while attempting to create the article. Please try again later.`,
          'Error Creating Article'
        );
        return of(articleActions.createFailure({ error }));
      })
    )
  );

  // effect to update a article
  update$ = createEffect(() =>
    this.actions$.pipe(
      ofType(articleActions.update),
      switchMap(
        async ({ article }) => await this.articleService.update(article)
      ),
      switchMap((article) => {
        this.toastr.success(
          'Article details updated successfully. The information might take a while to refresh on user devices.',
          'Article Details Updated'
        );
        this.router.navigate(['/articles']);
        return of(articleActions.updateSuccess({ article }));
      }),
      catchError((error) => {
        // console.error(`Error updating article: ${error}`);
        this.toastr.error(
          `An error occurred while attempting to update the article. Please try again later.`,
          'Error Updating Article'
        );
        return of(articleActions.updateFailure({ error }));
      })
    )
  );

  // effect to delete a article
  delete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(articleActions.deleteAction),
      switchMap(
        async ({ id }) =>
          await this.articleService
            .delete(id)
            .then((resultId) => {
              this.toastr.success(
                'The article has been deleted successfully.',
                'Article Deleted'
              );
              this.router.navigate(['/articles']);
              return articleActions.deleteSuccess({ id: resultId });
            })
            .catch((error) => {
              this.toastr.error(
                `An error occurred while attempting to delete the article. Please try again later.`,
                'Error Deleting Article'
              );
              return articleActions.deleteFailure({ error: error.toString() });
            })
      )
    )
  );

  clearArticles$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(userActions.logoutSuccess),
      switchMap(() => {
        return of(articleActions.clear());
      })
    )
  );
}
